import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import ApiClient from '../Services/ApiClient';
import { Lead, LeadDto, LeadStatus } from '../../Models/Lead';

export interface LeadPagedResult {
  leads: Lead[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface LeadsState {
  paged: LeadPagedResult | null;
  byId: Record<number, Lead>;
  loading: boolean;
  error?: string | null;
}

const initialState: LeadsState = {
  paged: null,
  byId: {},
  loading: false,
  error: null
};

export const fetchLeadsPaged = createAsyncThunk(
  'leads/fetchPaged',
  async ({ page = 1, pageSize = 20, query, sortBy, sortDir }: { page?: number; pageSize?: number; query?: string; sortBy?: string; sortDir?: string }) => {
    const q = query ? `&query=${encodeURIComponent(query)}` : '';
    const res = await ApiClient.get(`/api/leads/paged?page=${page}&pageSize=${pageSize}${q}${sortBy ? `&sortBy=${encodeURIComponent(sortBy)}` : ''}${sortDir ? `&sortDir=${encodeURIComponent(sortDir)}` : ''}`);
    const payload = res && res.data ? res.data : res;
    return payload as LeadPagedResult;
  }
);

export const getLead = createAsyncThunk('leads/get', async (id: number) => {
  const res = await ApiClient.get(`/api/leads/${id}`);
  const payload = res && res.data ? res.data : res;
  return payload as Lead;
});

export const updateLeadStatus = createAsyncThunk('leads/updateStatus', async ({ id, status }: { id: number; status: LeadStatus }) => {
  await ApiClient.put(`/api/leads/${id}/status`, { status });
  return { id, status };
});

const slice = createSlice({
  name: 'leads',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeadsPaged.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLeadsPaged.fulfilled, (s, a) => {
        s.loading = false;
        s.paged = a.payload;
        s.byId = {};
        (a.payload?.leads || []).forEach(l => s.byId[l.id] = l);
      })
      .addCase(fetchLeadsPaged.rejected, (s, a) => { s.loading = false; s.error = a.error.message || 'Failed'; })

      .addCase(getLead.fulfilled, (s, a) => { s.byId[a.payload.id] = a.payload; })

      .addCase(updateLeadStatus.fulfilled, (s, a) => {
        const id = a.payload.id;
        const status = a.payload.status;
        if (s.byId[id]) s.byId[id].leadStatusId = status as any;
        if (s.paged) {
          const idx = s.paged.leads.findIndex(l => l.id === id);
          if (idx >= 0) s.paged.leads[idx].leadStatusId = status as any;
        }
      });
  }
});

export default slice.reducer;
